import { createContext, useContext, useEffect, useState } from 'react'
import { Session } from '@supabase/supabase-js'
import { supabase } from './supabaseClient'

interface UserProfile {
    id: number
    email: string
    fullname: string
    is_carlist: number
    is_newcar: number
    is_income: number
    is_expense: number
    is_view_sum: number
    is_export: number
    is_report: number
    is_setting: number
    is_invite: number
    role_name?: string
    company_id: number | null
    company_name?: string
}

interface AuthContextType {
    session: Session | null
    userProfile: UserProfile | null
    loading: boolean
    signOut: () => Promise<void>
    refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType>(undefined as any)

const API = process.env.NODE_ENV === 'development' ? "http://localhost:8080/api" : "/api"

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [session, setSession] = useState<Session | null>(null)
    const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
    const [loading, setLoading] = useState(true)

    async function syncUserProfile(session: Session) {
        try {
            const res = await fetch(API + '/v1/auth/me', {
                headers: { Authorization: `Bearer ${session.access_token}` }
            })
            if (res.ok) {
                const data = await res.json()
                setUserProfile(data.user)
            } else {
                await supabase.auth.signOut()
                setUserProfile(null)
                window.location.href = '/login'
                return
            }
        } catch (error) {
            console.error('Failed to sync user profile:', error)
            await supabase.auth.signOut()
            setUserProfile(null)
            window.location.href = '/login'
            return
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session)
            if (session) {
                syncUserProfile(session)
            } else {
                setLoading(false)
            }
        })

        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            setSession(session)
            if (event === 'PASSWORD_RECOVERY') {
                // หน้า /reset-password จัดการเอง — อย่า sync profile แล้วสะบัดออกกลาง flow
                setLoading(false)
                return
            }
            if (session) {
                syncUserProfile(session)
            } else {
                setUserProfile(null)
                setLoading(false)
            }
        })

        return () => subscription.unsubscribe()
    }, [])

    async function refreshProfile() {
        const { data: { session } } = await supabase.auth.getSession()
        if (session) {
            await syncUserProfile(session)
        }
    }

    async function signOut() {
        await supabase.auth.signOut()
        setUserProfile(null)
        try {
            await fetch('https://accounts.google.com/o/oauth2/revoke?token=' + (session?.provider_token || ''), { method: 'POST' })
        } catch {}
        window.location.href = '/login'
    }

    return (
        <AuthContext.Provider value={{ session, userProfile, loading, signOut, refreshProfile }}>
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    return useContext(AuthContext)
}
